import React, { useState } from "react";

const ActivationPromptPreviewWindow = ({
  cards,
  source, // 'profiles' or 'roles'
  targetEmail,
  emailIntro,
  emailOutro,
  onClose,
}) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendEmail = async () => {
    setSending(true);
    const token = localStorage.getItem("token");

    const cardHtml = cards.map((c) => {
      if (source === "profiles") {
        const interests = Array.isArray(c.interests)
          ? c.interests.slice(0, 3).join(", ")
          : c.interests || "No interests listed";

        return `
          <div style="display:flex;align-items:center;margin-bottom:16px;">
            <img src="${c.avatar || "https://frontera.app/person.png"}" alt="${c.firstName}" style="width:40px;height:40px;border-radius:50%;margin-right:12px;border:1px solid #ccc;" />
            <div>
              <p style="margin:0;font-weight:bold;color:#01579B;">${c.firstName} ${c.lastName}</p>
              <p style="margin:0;font-style:italic;font-size:12px;color:#666;">${interests}</p>
              <p style="margin:0;color:#2E7D32;font-weight:500;font-size:12px;">${c.matchScore || "?"}% match</p>
            </div>
          </div>
        `;
      } else {
        return `
          <div style="margin-bottom:16px;">
            <p style="margin:0;font-weight:bold;color:#01579B;">${c.roleTitle} @ ${c.projectTitle}</p>
            <p style="margin:0;font-style:italic;font-size:12px;color:#666;">${c.description || "No description"}</p>
            <p style="margin:0;color:#2E7D32;font-weight:500;font-size:12px;">${c.matchScore || "?"}% match</p>
          </div>
        `;
      }
    }).join("");

    const emailHtml = `
  <div style="font-family:Arial,sans-serif;padding:24px;line-height:1.6;background:#FEFCFF;">
    <div style="text-align:center;margin-bottom:20px;">
      <img src="https://frontera.app/CodiCharacter.png" alt="Codi" style="width:56px;height:56px;" />
      <p style="margin:6px 0 0;font-weight:bold;font-size:14px;color:#01579B;">
        Codi, your AI Matchmaker from Frontera
      </p>
    </div>

    <p style="margin-bottom:24px;color:#333;">${(emailIntro || "").replace(/\n/g, "<br/>")}</p>

    <div style="margin:24px 0;">${cardHtml}</div>

    <!-- 🔹 CALL TO ACTION BUTTON -->
    <div style="text-align:center; margin:32px 0;">
      <a href="https://frontera.app/login"
         style="background:#49C3FB;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;font-weight:bold;display:inline-block;">
        🚀 Go to Frontera
      </a>
    </div>

    <p style="margin-top:24px;color:#333;">${(emailOutro || "").replace(/\n/g, "<br/>")}</p>

    <hr style="margin:32px 0; border:none; border-top:1px solid #eee;" />
    <p style="font-size:12px;color:#888;text-align:center;">
      Sent by <strong style="color:#01579B;">Codi</strong>, your AI Matchmaker from 
      <a href="https://frontera.app" style="color:#49C3FB;text-decoration:none;">Frontera</a>.
    </p>
  </div>
`;


    const subject =
      source === "profiles"
        ? "👥 Your Top Matches Are Ready"
        : "💼 Explore Roles Picked for You";

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/codi/admin/emailman`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: targetEmail,
            preview: emailHtml,
            subject,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to send email");
      setSent(true);
    } catch (err) {
      console.error("❌ Email send failed:", err);
      alert("Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded shadow-lg w-[700px] max-h-[90vh] overflow-y-auto relative">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          ✖
        </button>

        <div className="flex flex-col items-center mb-4">
          <img
            src="/CodiCharacter.png"
            alt="Codi AI"
            className="w-12 h-12 mb-2"
          />
          <h2 className="text-xl font-bold">📬 Activation Email Preview</h2>
        </div>

        <p className="text-sm text-gray-500 mb-6">
          This email will be sent to <strong>{targetEmail}</strong>.
        </p>

        {emailIntro && (
          <p className="text-sm mb-4 text-gray-800 whitespace-pre-wrap">
            {emailIntro}
          </p>
        )}

        <ul className="space-y-3 mb-4">
          {source === "profiles"
            ? cards.map((user, index) => {
                const interestList = Array.isArray(user.interests)
                  ? user.interests.slice(0, 3).join(", ")
                  : user.interests;
                return (
                  <li
                    key={user.email || index}
                    className="bg-white border p-3 rounded flex items-center space-x-3"
                  >
                    <img
                      src={user.avatar || "/person.png"}
                      alt={`${user.firstName}'s avatar`}
                      className="w-10 h-10 rounded-full object-cover border border-gray-300"
                    />
                    <div>
                      <p className="text-sm font-semibold text-blue-700">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs italic text-gray-600 mb-1">
                        {interestList}
                      </p>
                      <p className="text-xs text-green-600 font-medium">
                        {user.matchScore}% match
                      </p>
                    </div>
                  </li>
                );
              })
            : cards.map((role, index) => (
                <li key={index} className="bg-white border p-3 rounded">
                  <p className="text-sm font-semibold text-blue-700">
                    {role.roleTitle} @ {role.projectTitle}
                  </p>
                  <p className="text-xs italic text-gray-600 mb-1">
                    {role.description || "No description"}
                  </p>
                  <p className="text-xs text-green-600 font-medium">
                    {role.matchScore}% match
                  </p>
                </li>
              ))}
        </ul>

        {emailOutro && (
          <p className="text-sm mt-4 text-gray-700 whitespace-pre-wrap">
            {emailOutro}
          </p>
        )}

        <div className="mt-6 flex justify-end">
          <button
            disabled={sending || sent}
            onClick={handleSendEmail}
            className={`px-4 py-2 text-white rounded ${
              sent
                ? "bg-green-500 cursor-default"
                : sending
                ? "bg-gray-400 cursor-wait"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {sent ? "✅ Email Sent" : sending ? "Sending..." : "📨 Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivationPromptPreviewWindow;
